// Contract Validator for GraphQL Schema
// Treats the API as a contract that can break over time
// Alerts when schema changes or validation fails

/**
 * GraphQL Schema Contract Definition
 * This represents our current understanding of the API contract
 * Update this when the backend schema changes
 */
export const GRAPHQL_CONTRACT = {
  // CreateQueueRecordInput contract
  CreateQueueRecordInput: {
    required: ["name", "status"],
    optional: ["data"],
    types: {
      name: "String!",
      status: "String!",
      data: "AWSJSON",
    },
    description: "Input for creating queue records",
  },

  // CreateQueueRecordResponse contract
  CreateQueueRecordResponse: {
    required: ["success"],
    optional: ["messageId", "error"],
    types: {
      success: "Boolean!",
      messageId: "String",
      error: "String",
    },
    description: "Response from creating queue records",
  },

  // QueueRecord contract
  QueueRecord: {
    required: ["id", "name", "status", "createdAt", "updatedAt"],
    optional: ["data", "messageId", "receiptHandle", "body", "attributes"],
    types: {
      id: "ID!",
      name: "String!",
      status: "String!",
      createdAt: "AWSDateTime!",
      updatedAt: "AWSDateTime!",
      data: "AWSJSON",
      messageId: "String",
      receiptHandle: "String",
      body: "String",
      attributes: "AWSJSON",
    },
    description: "Queue record structure",
  },

  // LeadInput contract
  LeadInput: {
    required: ["email", "name", "company", "source", "tenantId"],
    optional: [],
    types: {
      email: "String!",
      name: "String!",
      company: "String!",
      source: "String!",
      tenantId: "String!",
    },
    description: "Input for creating leads",
  },

  // LeadUpdateInput contract
  LeadUpdateInput: {
    required: ["leadId", "tenantId"],
    optional: ["status", "score", "enrichedData"],
    types: {
      leadId: "String!",
      status: "String",
      score: "Float",
      enrichedData: "String",
      tenantId: "String!",
    },
    description: "Input for updating leads",
  },

  // Lead contract
  Lead: {
    required: [
      "leadId",
      "email",
      "name",
      "company",
      "source",
      "status",
      "timestamp",
      "tenantId",
    ],
    optional: ["score", "enrichedData"],
    types: {
      leadId: "String!",
      email: "String!",
      name: "String!",
      company: "String!",
      source: "String!",
      status: "String!",
      score: "Float",
      enrichedData: "String",
      timestamp: "String!",
      tenantId: "String!",
    },
    description: "Lead structure",
  },
};

/**
 * Contract validation error types
 */
export const CONTRACT_ERROR_TYPES = {
  MISSING_REQUIRED_FIELD: "MISSING_REQUIRED_FIELD",
  UNEXPECTED_FIELD: "UNEXPECTED_FIELD",
  INVALID_TYPE: "INVALID_TYPE",
  INVALID_JSON: "INVALID_JSON",
  SCHEMA_MISMATCH: "SCHEMA_MISMATCH",
  CONTRACT_BREAK: "CONTRACT_BREAK",
};

/**
 * Validate input against the GraphQL contract
 * This is our contract enforcement mechanism
 */
export const validateGraphQLContract = (
  input,
  contractName = "CreateQueueRecordInput"
) => {
  const contract = GRAPHQL_CONTRACT[contractName];
  if (!contract) {
    throw new Error(`Unknown contract: ${contractName}`);
  }

  const errors = [];
  const warnings = [];
  const contractBreaks = [];

  // Check for missing required fields
  contract.required.forEach((field) => {
    if (!(field in input)) {
      errors.push({
        type: CONTRACT_ERROR_TYPES.MISSING_REQUIRED_FIELD,
        field,
        message: `Required field '${field}' is missing`,
        severity: "ERROR",
      });
    }
  });

  // Check for unexpected fields
  const allowedFields = [...contract.required, ...contract.optional];
  Object.keys(input).forEach((field) => {
    if (!allowedFields.includes(field)) {
      errors.push({
        type: CONTRACT_ERROR_TYPES.UNEXPECTED_FIELD,
        field,
        message: `Unexpected field '${field}' not in contract`,
        severity: "ERROR",
      });
    }
  });

  // Validate field types
  Object.entries(input).forEach(([field, value]) => {
    if (field in contract.types) {
      const expectedType = contract.types[field];

      // Type validation
      if (expectedType === "String!" || expectedType === "String") {
        if (typeof value !== "string") {
          errors.push({
            type: CONTRACT_ERROR_TYPES.INVALID_TYPE,
            field,
            expected: expectedType,
            actual: typeof value,
            message: `Field '${field}' should be string, got ${typeof value}`,
            severity: "ERROR",
          });
        }
      }

      // JSON validation for AWSJSON fields
      if (expectedType === "AWSJSON" && value !== null && value !== undefined) {
        try {
          JSON.parse(value);
        } catch (error) {
          errors.push({
            type: CONTRACT_ERROR_TYPES.INVALID_JSON,
            field,
            message: `Field '${field}' must be valid JSON string`,
            severity: "ERROR",
          });
        }
      }
    }
  });

  // Check for potential contract breaks
  if (errors.length > 0) {
    contractBreaks.push({
      type: CONTRACT_ERROR_TYPES.CONTRACT_BREAK,
      contract: contractName,
      errors,
      message: `Contract validation failed for ${contractName}`,
      severity: "CRITICAL",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    contractBreaks,
    contract: contractName,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Log contract validation results with detailed information
 */
export const logContractValidation = (validationResult, context = "") => {
  const { isValid, errors, warnings, contractBreaks, contract, timestamp } =
    validationResult;

  console.group(`📋 Contract Validation: ${contract} ${context}`);
  console.log(`⏰ Timestamp: ${timestamp}`);
  console.log(`✅ Valid: ${isValid}`);

  if (errors.length > 0) {
    console.group("❌ Validation Errors:");
    errors.forEach((error) => {
      console.error(`  ${error.type}: ${error.message}`);
      console.error(`    Field: ${error.field}`);
      if (error.expected) {
        console.error(`    Expected: ${error.expected}, Got: ${error.actual}`);
      }
    });
    console.groupEnd();
  }

  if (warnings.length > 0) {
    console.group("⚠️ Warnings:");
    warnings.forEach((warning) => {
      console.warn(`  ${warning.message}`);
    });
    console.groupEnd();
  }

  if (contractBreaks.length > 0) {
    console.group("🚨 Contract Breaks:");
    contractBreaks.forEach((break_) => {
      console.error(`  ${break_.message}`);
      console.error(`    Contract: ${break_.contract}`);
      console.error(`    Errors: ${break_.errors.length}`);
    });
    console.groupEnd();
  }

  console.groupEnd();

  return validationResult;
};

/**
 * Alert system for contract breaks
 * This can be extended to send notifications, create tickets, etc.
 */
export const alertContractBreak = (validationResult) => {
  const { contractBreaks, contract } = validationResult;

  if (contractBreaks.length > 0) {
    // Log to console for immediate visibility
    console.error(`🚨 CONTRACT BREAK DETECTED: ${contract}`);
    console.error("This may indicate a backend schema change or frontend bug.");
    console.error("Please investigate and update the contract if necessary.");

    // TODO: Extend this to send alerts
    // - Send to monitoring service (e.g., Sentry, DataDog)
    // - Create JIRA ticket
    // - Send Slack notification
    // - Email team

    return {
      alerted: true,
      contract,
      breaks: contractBreaks,
      timestamp: new Date().toISOString(),
    };
  }

  return { alerted: false };
};

/**
 * Test the contract validation system
 */
export const testContractValidation = () => {
  console.group("🧪 Testing Contract Validation");

  // Test 1: Valid contract
  const validInput = {
    name: "Test Queue Record",
    status: "pending",
    data: '{"test": "data"}',
  };

  const validResult = validateGraphQLContract(
    validInput,
    "CreateQueueRecordInput"
  );
  logContractValidation(validResult, "(Valid Input)");

  // Test 2: Invalid contract (missing required field)
  const invalidInput1 = {
    name: "Test Queue Record",
    // Missing status
    data: '{"test": "data"}',
  };

  const invalidResult1 = validateGraphQLContract(
    invalidInput1,
    "CreateQueueRecordInput"
  );
  logContractValidation(invalidResult1, "(Missing Required Field)");
  alertContractBreak(invalidResult1);

  // Test 3: Invalid contract (unexpected field)
  const invalidInput2 = {
    name: "Test Queue Record",
    status: "pending",
    data: '{"test": "data"}',
    unexpectedField: "should not be here",
  };

  const invalidResult2 = validateGraphQLContract(
    invalidInput2,
    "CreateQueueRecordInput"
  );
  logContractValidation(invalidResult2, "(Unexpected Field)");
  alertContractBreak(invalidResult2);

  // Test 4: Invalid contract (invalid JSON)
  const invalidInput3 = {
    name: "Test Queue Record",
    status: "pending",
    data: "invalid json string",
  };

  const invalidResult3 = validateGraphQLContract(
    invalidInput3,
    "CreateQueueRecordInput"
  );
  logContractValidation(invalidResult3, "(Invalid JSON)");
  alertContractBreak(invalidResult3);

  console.groupEnd();
};

/**
 * Update contract when backend schema changes
 * Call this when you need to update the contract definition
 */
export const updateContract = (contractName, newContract) => {
  console.log(`🔄 Updating contract: ${contractName}`);
  console.log("Previous contract:", GRAPHQL_CONTRACT[contractName]);
  console.log("New contract:", newContract);

  GRAPHQL_CONTRACT[contractName] = newContract;

  console.log(`✅ Contract updated: ${contractName}`);
  console.log("⚠️ Remember to update documentation and tests");
};
